import {
  CartDiscountCode,
  CartDto,
  CartSummaryLineDto,
  IntegrationReservation,
} from '../infrastructure/dto/products.dto';

export interface CartStatus {
  id: string;
  reservationId: string;
  total: number;
  pending_amount: number;
  /** Backend-computed recurring per-month total; 0 when the cart has no subscription lines. */
  total_recurring_monthly: number;
  server_time: string;
  reserved_until: string | null;
  items: CartItem[];

  requiresAccount: boolean | null;
  status: CartDto['status'];
  lines: CartSummaryLineDto[];
  integration_reservations: IntegrationReservation[];
  discounts: CartDiscountCode[];
}

export interface CartItem {
  parentCartLineId: string | null;
  productId: string;
  title: string;
  amount: number;
  quantity: number;
  total: number;
  lines: CartSummaryLineDto[];
  type: number;
  discountName: string | null;
  discountedAmount: number | null;
}

export interface RowDiscount {
  discountName: string | null;
  discountedAmount: number | null;
}

/**
 * The discount the backend hung on one cart-preview row — its own lines plus the cost lines it
 * generated under them, which are not cart products and so have no row of their own (a ticket's
 * `Servicekosten`).
 *
 * A discount arrives as one negative line per discounted line, parented on it. A 100% discount on a
 * €12.50 ticket is therefore `-12.00` on the ticket line and `-0.50` on its service-cost line, and
 * counting only the lines whose product is the row's made it read as `-€12.00` against a €12.50 row
 * (PAUL-3468). A child line that is a cart product itself — a supplement — is left out: it is drawn
 * as its own row and carries its own discount there.
 */
export function resolveRowDiscount(
  rootLineIds: string[],
  lines: CartSummaryLineDto[],
  discounts: CartDiscountCode[],
  rowProductIds: ReadonlySet<string>
): RowDiscount {
  const ownedLineIds = claimCostLines(rootLineIds, lines, rowProductIds);
  const discountLines = lines.filter((line) => line.discount_code_id && ownedLineIds.has(line.parent_cart_line_id));

  if (discountLines.length === 0) {
    return { discountName: null, discountedAmount: null };
  }

  return {
    discountName: discounts.find((d) => d.discount_code_id === discountLines[0].discount_code_id)?.name ?? null,
    discountedAmount: discountLines.reduce((sum, line) => sum + line.amount, 0),
  };
}

/** The row's own lines, grown down the parent chain over every cost line hanging off them. */
function claimCostLines(
  rootLineIds: string[],
  lines: CartSummaryLineDto[],
  rowProductIds: ReadonlySet<string>
): Set<string> {
  // A row still being reserved has no line id yet; keeping it out means a parentless line is never
  // claimed by it.
  const owned = new Set(rootLineIds.filter((id) => !!id));
  const costLines = lines.filter(
    (line) => line.parent_cart_line_id && !line.discount_code_id && !rowProductIds.has(line.product_id)
  );

  let claimed: boolean;
  do {
    claimed = false;
    costLines.forEach((line) => {
      if (!owned.has(line.id) && owned.has(line.parent_cart_line_id)) {
        owned.add(line.id);
        claimed = true;
      }
    });
  } while (claimed);

  return owned;
}
